import React from "react";
import "../wizard.component.scoped.scss";
import { Box, TextField, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import {
  CreateWorkspaceWizardData,
  WizardStepProps,
} from "../../../types/create-workspace-wizard";

type Props = WizardStepProps<CreateWorkspaceWizardData["step1"]>;

const WizardStep1: React.FC<Props> = ({ data, onChange, errors }) => {
  const { t } = useTranslation();

  const [touched, setTouched] = React.useState<
    Partial<Record<keyof CreateWorkspaceWizardData["step1"], boolean>>
  >({});

  const updateField = (
    field: keyof CreateWorkspaceWizardData["step1"],
    value: string,
  ) => {
    setTouched(
      (
        prev: Partial<
          Record<keyof CreateWorkspaceWizardData["step1"], boolean>
        >,
      ) => ({ ...prev, [field]: true }),
    );
    onChange({ ...data, [field]: value });
  };

  return (
    <>
      <Box
        className="global__box__content product__details__content__form p-0 mb-5"
        style={{ height: "fit-content" }}
      >
        <Box className="global__box__content__header">
          <Typography
            className="text-capitalize"
            variant="h3"
            color="textPrimary"
          >
            {t("WORKSPACE_WIZARD.COMPANY_INFORMATION")}
          </Typography>
        </Box>
        <Box className="global__box__content__body">
          <Box className="form__item">
            <Typography
              variant="subtitle2"
              color="textPrimary"
              className="text-capitalize"
            >
              {t("WORKSPACE_WIZARD.COMPANY_NAME")}
            </Typography>

            <Box className="standard_text_input">
              <TextField
                className="w-100"
                placeholder={t("WORKSPACE_WIZARD.COMPANY_NAME")}
                variant="standard"
                inputProps={{
                  maxLength: 30,
                }}
                name="name"
                value={data.name}
                onChange={(e) => updateField("name", e.target.value)}
              />
              {touched.name && errors?.name && (
                <Typography
                  variant="body2"
                  className="mt-2"
                  color="error"
                  style={{ fontWeight: 300 }}
                >
                  {errors.name === "REQUIRED"
                    ? t("THIS_FIELD_IS_REQUIRED")
                    : t(
                        "INVALID_FORMAT_ONLY_SPECIAL_CHARACTERS_STRINGS_ALLOWED",
                      )}
                </Typography>
              )}
            </Box>
          </Box>

          <Box className="form__item">
            <Typography variant="subtitle2">
              {t("WORKSPACE_WIZARD.COMPANY_REGISTER_NUMBER")}
            </Typography>
            <Box className="standard_text_input">
              <TextField
                className="w-100"
                placeholder={t("WORKSPACE_WIZARD.COMPANY_REGISTER_NUMBER")}
                variant="standard"
                type="text"
                name="register_number"
                value={data.register_number}
                onChange={(e) => updateField("register_number", e.target.value)}
                inputProps={{
                  maxLength: 30,
                }}
              />
              {touched.register_number && errors?.register_number && (
                <Typography
                  variant="body2"
                  className="mt-2"
                  color="error"
                  style={{ fontWeight: 300 }}
                >
                  {errors.register_number === "REQUIRED"
                    ? t("THIS_FIELD_IS_REQUIRED")
                    : t("INVALID_REGISTER_NUMBER_FORMAT")}
                </Typography>
              )}
            </Box>
          </Box>

          <Box className="form__item">
            <Typography variant="subtitle2">
              {t("WORKSPACE_WIZARD.COMPANY_ADDRESS")}
            </Typography>
            <Box className="standard_text_input">
              <TextField
                className="w-100"
                placeholder={t("WORKSPACE_WIZARD.COMPANY_ADDRESS")}
                variant="standard"
                name="address"
                value={data.address}
                inputProps={{
                  maxLength: 30,
                }}
                onChange={(e) => updateField("address", e.target.value)}
              />
              {touched.address && errors?.address && (
                <Typography
                  variant="body2"
                  className="mt-2"
                  color="error"
                  style={{ fontWeight: 300 }}
                >
                  {errors.address === "REQUIRED"
                    ? t("THIS_FIELD_IS_REQUIRED")
                    : t(
                        "INVALID_FORMAT_ONLY_SPECIAL_CHARACTERS_STRINGS_ALLOWED",
                      )}
                </Typography>
              )}
            </Box>
          </Box>

          <Box className="form__item">
            <Typography variant="subtitle2">
              {t("WORKSPACE_WIZARD.COMPANY_POSTAL_CODE")}
            </Typography>
            <Box className="standard_text_input">
              <TextField
                placeholder={t("WORKSPACE_WIZARD.COMPANY_POSTAL_CODE")}
                variant="standard"
                name="postal_code"
                value={data.postal_code}
                className="w-100"
                onChange={(e) => updateField("postal_code", e.target.value)}
              />
              {touched.postal_code && errors?.postal_code && (
                <Typography
                  variant="body2"
                  className="mt-2"
                  color="error"
                  style={{ fontWeight: 300 }}
                >
                  {errors.postal_code === "REQUIRED"
                    ? t("THIS_FIELD_IS_REQUIRED")
                    : t("INVALID_POSTAL_CODE_FORMAT")}
                </Typography>
              )}
            </Box>
          </Box>

          <Box className="form__item">
            <Typography variant="subtitle2">
              {t("WORKSPACE_WIZARD.COMPANY_CITY")}
            </Typography>
            <Box className="standard_text_input">
              <TextField
                placeholder={t("WORKSPACE_WIZARD.COMPANY_CITY")}
                variant="standard"
                name="city"
                value={data.city}
                className="w-100"
                inputProps={{
                  maxLength: 30,
                }}
                onChange={(e) => updateField("city", e.target.value)}
              />
              {touched.city && errors?.city && (
                <Typography
                  variant="body2"
                  className="mt-2"
                  color="error"
                  style={{ fontWeight: 300 }}
                >
                  {errors.city === "REQUIRED"
                    ? t("THIS_FIELD_IS_REQUIRED")
                    : t(
                        "INVALID_FORMAT_ONLY_SPECIAL_CHARACTERS_STRINGS_ALLOWED",
                      )}
                </Typography>
              )}
            </Box>
          </Box>

          <Box className="form__item">
            <Typography variant="subtitle2">
              {t("WORKSPACE_WIZARD.COMPANY_EMAIL")}
            </Typography>
            <Box className="standard_text_input">
              <TextField
                placeholder={t("WORKSPACE_WIZARD.COMPANY_EMAIL")}
                variant="standard"
                name="email"
                value={data.email}
                className="w-100"
                onChange={(e) => updateField("email", e.target.value)}
              />
              {touched.email && errors?.email && (
                <Typography
                  variant="body2"
                  className="mt-2"
                  color="error"
                  style={{ fontWeight: 300 }}
                >
                  {errors.email === "REQUIRED"
                    ? t("THIS_FIELD_IS_REQUIRED")
                    : t("INVALID_EMAIL_FORMAT")}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        className="global__box__content product__details__content__form p-0"
        style={{ height: "fit-content" }}
      >
        <Box className="global__box__content__header">
          <Typography
            className="text-capitalize"
            variant="h3"
            color="textPrimary"
          >
            {t("WORKSPACE_WIZARD.CONTACT_INFORMATION")}
          </Typography>
        </Box>
        <Box className="global__box__content__body">
          <Box className="form__item">
            <Typography variant="subtitle2">
              {t("WORKSPACE_WIZARD.TECHNICAL_CONTACT_EMAIL")}
            </Typography>
            <Box className="standard_text_input">
              <TextField
                placeholder={t("WORKSPACE_WIZARD.TECHNICAL_CONTACT_EMAIL")}
                variant="standard"
                name="technical_contact_email"
                value={data.technical_contact_email}
                className="w-100"
                onChange={(e) =>
                  updateField("technical_contact_email", e.target.value)
                }
              />
              {touched.technical_contact_email &&
                errors?.technical_contact_email && (
                  <Typography
                    variant="body2"
                    className="mt-2"
                    color="error"
                    style={{ fontWeight: 300 }}
                  >
                    {errors.technical_contact_email === "REQUIRED"
                      ? t("THIS_FIELD_IS_REQUIRED")
                      : t("INVALID_EMAIL_FORMAT")}
                  </Typography>
                )}
            </Box>
          </Box>

          <Box className="form__item">
            <Typography variant="subtitle2">
              {t("WORKSPACE_WIZARD.ORGANIZATION_CONTACT_EMAIL")}
            </Typography>
            <Box className="standard_text_input">
              <TextField
                placeholder={t("WORKSPACE_WIZARD.ORGANIZATION_CONTACT_EMAIL")}
                variant="standard"
                name="organization_contact_email"
                value={data.organization_contact_email}
                className="w-100"
                onChange={(e) =>
                  updateField("organization_contact_email", e.target.value)
                }
              />
              {touched.organization_contact_email &&
                errors?.organization_contact_email && (
                  <Typography
                    variant="body2"
                    className="mt-2"
                    color="error"
                    style={{ fontWeight: 300 }}
                  >
                    {errors.organization_contact_email === "REQUIRED"
                      ? t("THIS_FIELD_IS_REQUIRED")
                      : t("INVALID_EMAIL_FORMAT")}
                  </Typography>
                )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default WizardStep1;
